import * as React from 'react';
import { Well, Label, Table } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import Pagination from '../../react-common/Pagination';
import SearchInput from '../../react-common/SearchInput';

const STATE_NAME_MAP = {
  users: ['selectedUsers', 'selectedUsersMap'],
  groups: ['selectedGroups', 'selectedGroupsMap'],
  teams: ['selectedGroups', 'selectedGroupsMap'],
  locations: ['selectedLocations', 'selectedLocationsMap'],
};

type AnnouncementRecipientsState = {
  omittedUsers: object[];
  selectedUsers: object[];
  selectedUsersMap: Record<string, boolean>;
  selectedGroups: object[];
  selectedGroupsMap: Record<string, boolean>;
  selectedLocations: object[];
  selectedLocationsMap: Record<string, boolean>;
  activeSearchKey: 'groups' | 'teams' | 'users' | 'locations';
  uniqueUsersPage: number;
  activeTableKey: 'recipients' | 'omitted';
  userTypeFilters: string[];
  [x: string]: any;
};

// TODO: Move state to parent component;
// Currently we are using the onRecipientsUpdate callback to reflect state to parent
// Or we can just view this as a controlled form
class AnnouncementRecipients extends React.Component<
  any,
  AnnouncementRecipientsState
> {
  constructor(props: any) {
    super(props);
    const { recipients } = props;
    this.state = {
      selectedUsers: [],
      selectedUsersMap: {},
      selectedGroups: [],
      selectedGroupsMap: {},
      selectedLocations: [],
      selectedLocationsMap: {},
      omittedUsers: [],
      userTypeFilters: [],
      activeSearchKey: 'locations',
      activeTableKey: 'recipients',
      ...recipients,
    };
  }

  onItemSelect = (item: any) => (event: React.MouseEvent) => {
    event.preventDefault();
    const { activeSearchKey } = this.state;
    const [itemsArrayName, itemsMapName] = STATE_NAME_MAP[activeSearchKey];
    const { [itemsArrayName]: currentItems, [itemsMapName]: currentMap } =
      this.state;
    document.getElementById('search-input-field').focus();
    const mapField = activeSearchKey === 'locations' ? 'name' : 'id';
    // Do not allow duplicated item
    if (currentMap[item[mapField]]) return;
    this.setState(
      {
        [itemsArrayName]: [...currentItems, item],
        [itemsMapName]: { ...currentMap, [item[mapField]]: true },
      },
      this.updateParentComponent,
    );
  };

  onItemRemove =
    (index: number, type: AnnouncementRecipientsState['activeSearchKey']) =>
    (event: React.MouseEvent<Label, MouseEvent> | React.MouseEvent) => {
      event.preventDefault();
      const [itemsArrayName, itemsMapName] = STATE_NAME_MAP[type];
      const { [itemsArrayName]: currentItems, [itemsMapName]: currentMap } =
        this.state;
      const { id, name } = currentItems[index];
      const updatedMap = { ...currentMap };
      delete updatedMap[id || name];
      const updatedItems = [...currentItems];
      updatedItems.splice(index, 1);
      this.setState(
        { [itemsArrayName]: updatedItems, [itemsMapName]: updatedMap },
        this.updateParentComponent,
      );
    };

  addOmittedUsers =
    (user: any) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const { omittedUsers } = this.state;
      this.setState(
        { omittedUsers: [...omittedUsers, user] },
        this.updateParentComponent,
      );
    };

  removeOmittedUsers =
    (user: any, index: number) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const { omittedUsers } = this.state;
      const updatedOmittedUsers = [...omittedUsers];
      updatedOmittedUsers.splice(index, 1);
      this.setState(
        { omittedUsers: updatedOmittedUsers },
        this.updateParentComponent,
      );
    };

  addUserTypeFilter = (userType: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    const { userTypeFilters } = this.state;
    if (userTypeFilters.includes(userType)) return;
    this.setState(
      { userTypeFilters: [...userTypeFilters, userType] },
      this.updateParentComponent,
    );
  };

  removeUserTypeFilter =
    (index: number) => (event: React.MouseEvent<Label, MouseEvent>) => {
      event.preventDefault();
      const { userTypeFilters } = this.state;
      const updatedUserTypeFilters = [...userTypeFilters];
      updatedUserTypeFilters.splice(index, 1);
      this.setState(
        { userTypeFilters: updatedUserTypeFilters },
        this.updateParentComponent,
      );
    };

  onSearchTypeSelect =
    (value: AnnouncementRecipientsState['activeSearchKey']) =>
    (event: React.MouseEvent) => {
      event.preventDefault();
      this.setState({ activeSearchKey: value }, this.updateParentComponent);
    };

  onTableTypeSelect =
    (value: AnnouncementRecipientsState['activeTableKey']) =>
    (event: React.MouseEvent) => {
      event.preventDefault();
      this.setState({ activeTableKey: value });
    };

  isSearchTypeActive = (value: string) => {
    const { activeSearchKey } = this.state;
    return value === activeSearchKey ? 'active' : '';
  };

  isTableTypeActive = (value: string) => {
    const { activeTableKey } = this.state;
    return value === activeTableKey ? 'active' : '';
  };

  updateParentComponent = () => {
    const { onRecipientsUpdate } = this.props;
    onRecipientsUpdate(this.state);
  };

  render() {
    const {
      uniqueUsers,
      uniqueCount,
      changeUniqueUsersPage,
      uniqueUsersPage,
      uniqueUsersPerPage,
      isLoading,
      locations,
      authorizedLocations,
    } = this.props;
    const {
      activeSearchKey,
      selectedGroups,
      selectedLocations,
      omittedUsers,
      activeTableKey,
      userTypeFilters,
    } = this.state;
    const [, activeMapName] = STATE_NAME_MAP[activeSearchKey];
    const { [activeMapName]: activeMap } = this.state;
    const isRecipientsActive = activeTableKey === 'recipients';
    const activeTableArray = isRecipientsActive ? uniqueUsers : omittedUsers;
    const onUserClick = isRecipientsActive
      ? this.addOmittedUsers
      : this.removeOmittedUsers;
    const paginationClass = isRecipientsActive
      ? 'recipients-paginate'
      : 'recipients-paginate hidden';
    return (
      <div className="announcement-recipients-container">
        <div className="recipient-input-container">
          <ul className="nav nav-pills" style={{ marginBottom: '1em' }}>
            <li role="presentation" className="disabled">
              <a>
                <i className="fa fa-search" />
              </a>
            </li>
            <li
              role="presentation"
              onClick={this.onSearchTypeSelect('locations')}
              className={this.isSearchTypeActive('locations')}
            >
              <a>Branches</a>
            </li>
            <li
              role="presentation"
              onClick={this.onSearchTypeSelect('teams')}
              className={this.isSearchTypeActive('teams')}
            >
              <a>Teams</a>
            </li>
            <li
              role="presentation"
              onClick={this.onSearchTypeSelect('groups')}
              className={this.isSearchTypeActive('groups')}
            >
              <a>Groups</a>
            </li>
            <li
              role="presentation"
              onClick={this.onSearchTypeSelect('users')}
              className={this.isSearchTypeActive('users')}
            >
              <a>Users</a>
            </li>
          </ul>
          <SearchInput
            locations={locations}
            authorizedLocations={authorizedLocations}
            type={activeSearchKey}
            selectedIds={activeMap}
            onItemSelect={this.onItemSelect}
          />
        </div>
        <div className="recipients-selected-container">
          <p>
            <strong>{uniqueCount}</strong> Total Recipient(s) Selected
          </p>
          <div className="btn-group pull-right filter-dropdown">
            <button
              type="button"
              className="btn btn-xs btn-default dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <i className="fa fa-filter"></i> Filters{' '}
              <span className="caret"></span>
            </button>
            <ul className="dropdown-menu">
              <li>
                <a onClick={this.addUserTypeFilter('Global Admins')}>
                  Remove Global Admins
                </a>
              </li>
              <li>
                <a onClick={this.addUserTypeFilter('Branch Admins')}>
                  Remove Branch Admins
                </a>
              </li>
              <li>
                <a onClick={this.addUserTypeFilter('Team Admins')}>
                  Remove Team Admins
                </a>
              </li>
              <li>
                <a onClick={this.addUserTypeFilter('Agents')}>Remove Agents</a>
              </li>
            </ul>
          </div>
          <Well bsSize="lg">
            {selectedGroups.map((group: any, i) => (
              <Label
                key={uuidv4()}
                bsStyle={group.type === 'team' ? 'primary' : 'default'}
                role="button"
                onClick={this.onItemRemove(i, 'groups')}
              >
                {group.name} <i className="fa fa-times"></i>
              </Label>
            ))}
            {selectedLocations.map((location: any, i) => (
              <Label
                key={uuidv4()}
                bsStyle="info"
                role="button"
                onClick={this.onItemRemove(i, 'locations')}
              >
                {location.name} <i className="fa fa-times"></i>
              </Label>
            ))}
            {userTypeFilters.map((userType: string, i: number) => (
              <Label
                key={uuidv4()}
                bsStyle="danger"
                role="button"
                onClick={this.removeUserTypeFilter(i)}
              >
                <i className="fa fa-filter"></i> No {userType}{' '}
                <i className="fa fa-times"></i>
              </Label>
            ))}
          </Well>
          {/* Table Tabs */}
          <ul className="nav nav-tabs table-tabs">
            <li
              role="presentation"
              onClick={this.onTableTypeSelect('recipients')}
              className={this.isTableTypeActive('recipients')}
            >
              <a>Recipients</a>
            </li>
            <li
              role="presentation"
              onClick={this.onTableTypeSelect('omitted')}
              className={this.isTableTypeActive('omitted')}
            >
              <a>Omitted Users ({omittedUsers.length})</a>
            </li>
          </ul>
          {/* Table Pagination */}
          <div className={paginationClass}>
            <Pagination
              itemsPerPage={uniqueUsersPerPage}
              totalItems={uniqueCount}
              currentPage={uniqueUsersPage}
              maxSize={5}
              onChange={changeUniqueUsersPage}
            />
          </div>
          {/* Recipient / Omitted Users Table Break into component? */}
          <Table className="recipients-table" striped bordered hover>
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Branch</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeTableArray.map((user: any, index: number) => (
                <tr key={user.id}>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{locations[user.location]?.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <button
                      disabled={isLoading}
                      onClick={onUserClick(user, index)}
                      className={
                        isRecipientsActive
                          ? 'btn btn-danger btn-xs'
                          : 'btn btn-info btn-xs'
                      }
                    >
                      <i
                        className={
                          isRecipientsActive ? 'fa fa-times' : 'fa fa-undo'
                        }
                      ></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  }
}

export default AnnouncementRecipients;
